/**
 * WEB COPY of LetterPaywallScreen.tsx — picked by the web bundler ahead of
 * the mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Same offer, same copy, same plan cards. The phone buys through the app
 * store; the web buys through Stripe Checkout (decision D4). App Store
 * pricing is never mentioned here. When LetterPaywallScreen.tsx changes,
 * re-apply the change here.
 */
import { useNavigation } from '@react-navigation/native';
import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { billingService } from '../../services/billingService';
import { ms, s } from '../../utils/responsive';


const BENEFITS = [
  'Name the people who receive it',
  'Choose when it reaches them',
  'Choose what triggers release',
];

export const LetterPaywallScreen: React.FC = () => {
  const navigation = useNavigation<any>();

  const [isAnnual, setIsAnnual] = useState(true);

  // Stripe hosts the checkout page; the browser goes there and comes back
  // to the Billing screen. If the letter tier has no web price yet, say so.
  const [starting, setStarting] = useState(false);
  const handleUnlock = async () => {
    if (starting) return;
    try {
      setStarting(true);
      await billingService.startCheckout('letter', isAnnual ? 'yearly' : 'monthly');
    } catch (error: any) {
      setStarting(false);
      Alert.alert(
        'Checkout',
        error?.response?.data?.message ||
          'Web checkout is not available right now. You can also choose a plan in the FinalKey app on your phone — your letter is safe either way.',
      );
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <Text style={styles.title}>
            You wrote something worth keeping. Now make sure it lands.
          </Text>
          <Text style={styles.subtitle}>
            Right now it lives here, and only you can open it. Delivery is the
            part that makes sure it reaches them.
          </Text>

          <View style={styles.benefitsCard}>
            {BENEFITS.map((b) => (
              <View key={b} style={styles.benefitRow}>
                <Image
                  source={require('../../assets/icons/check.png')}
                  style={styles.benefitIcon}
                />
                <Text style={styles.benefitText}>{b}</Text>
              </View>
            ))}
          </View>

          <TouchableOpacity
            style={[styles.planCard, isAnnual && styles.planCardSelected]}
            onPress={() => setIsAnnual(true)}
            accessibilityRole="button"
            accessibilityLabel="Annual plan, $9.99 per year"
            accessibilityState={{ selected: isAnnual }}
          >
            <View style={styles.planHeader}>
              <Text style={styles.planName}>Annual</Text>
              <View style={styles.bestValue}>
                <Text style={styles.bestValueText}>Best value</Text>
              </View>
            </View>
            <Text style={styles.planPrice}>$9.99/year</Text>
            <Text style={styles.planHint}>About 83¢ a month</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.planCard, !isAnnual && styles.planCardSelected]}
            onPress={() => setIsAnnual(false)}
            accessibilityRole="button"
            accessibilityLabel="Monthly plan, 99 cents per month"
            accessibilityState={{ selected: !isAnnual }}
          >
            <Text style={styles.planName}>Monthly</Text>
            <Text style={styles.planPrice}>$0.99/month</Text>
            <Text style={styles.planHint}>Cancel any time</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.unlockButton, starting && styles.dim]}
            onPress={handleUnlock}
            disabled={starting}
            accessibilityRole="button"
            accessibilityLabel={`Unlock delivery, ${isAnnual ? '$9.99 per year' : '99 cents per month'}`}
          >
            <Text style={styles.unlockButtonText}>
              Unlock delivery — {isAnnual ? '$9.99/year' : '$0.99/month'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.keepButton}
            onPress={() => navigation.navigate('LetterLevels')}
            accessibilityRole="button"
            accessibilityLabel="Keep to myself for now"
          >
            <Text style={styles.keepButtonText}>Keep to myself for now</Text>
          </TouchableOpacity>

          <Text style={styles.footnote}>
            Cancel any time. Your letter stays yours either way.
          </Text>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerSpacer: {
    flex: 1,
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(24),
    paddingHorizontal: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: '#6B7280',
    marginBottom: ms(16),
  },
  benefitsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(16),
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(10),
    marginBottom: ms(8),
  },
  benefitIcon: {
    width: s(18),
    height: s(18),
    tintColor: '#29BFC0',
  },
  benefitText: {
    flex: 1,
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  planCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(2),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(10),
  },
  planCardSelected: {
    borderColor: '#29BFC0',
    backgroundColor: '#F0FBFB',
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  planName: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  bestValue: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(12),
    paddingHorizontal: ms(10),
    paddingVertical: ms(4),
  },
  bestValueText: {
    color: '#FFFFFF',
    fontSize: ms(11, 0.3),
    fontWeight: '600',
  },
  planPrice: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
    marginTop: ms(4),
  },
  planHint: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    marginTop: ms(2),
  },
  unlockButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
    marginTop: ms(6),
  },
  unlockButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  keepButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
    borderRadius: ms(25),
    borderWidth: ms(1.5),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    marginTop: ms(10),
  },
  keepButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  footnote: {
    fontSize: ms(12, 0.3),
    color: '#A9A9A9',
    textAlign: 'center',
    marginTop: ms(12),
  },
  bottomPadding: {
    height: s(40),
  },
  dim: {
    opacity: 0.6,
  },
});

export default LetterPaywallScreen;
